// Shared native WebSocket client for the OCR ws-server.
//
// Protocol probe results (2026-05-13):
//   wss://eroyal.pmdgtech.com/ws                  → 101 Switching Protocols
//   wss://eroyal.pmdgtech.com/ws/socket.io/...    → 403 Forbidden
// So the production endpoint speaks the WebSocket protocol directly — it
// is NOT a socket.io server. This module mirrors the ocr-ui reference
// implementation (ocr-ui/src/socket/websocket.js): a plain WebSocket,
// JSON-encoded message frames, no socket.io framing.
//
// Multiple hooks share one underlying connection via reference counting
// so adding a new consumer doesn't open another browser↔server socket.
// The wrapper exposes a tiny pub/sub surface (`onMessage`, `onState`,
// `send`) so consumers don't have to juggle the WebSocket lifecycle.

const OIDC_STORAGE_KEY =
  "oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app";

// Default to the eroyal endpoint that responded 101 to a raw WS upgrade.
// Override with VITE_OCR_WS_URL to point at a local docker-compose stack
// or a staging cluster.
const WS_URL =
  (import.meta.env.VITE_OCR_WS_URL as string | undefined) ??
  "wss://pmdgtech.com/ws/ocr";

// Some servers expect the OIDC bearer as a query string param (native
// WebSocket cannot set Authorization headers from the browser). Off by
// default to match the ocr-ui reference, which connects unauthenticated.
// Set VITE_OCR_WS_AUTH_PARAM=token to opt in — the param name becomes the
// key, e.g. `?token=<jwt>`.
const WS_AUTH_PARAM =
  (import.meta.env.VITE_OCR_WS_AUTH_PARAM as string | undefined) ?? "";

const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 15_000;
const RECONNECT_MAX_ATTEMPTS = 10;

export type ConnectionState = "connecting" | "open" | "closed" | "error";

type MessageListener = (payload: unknown, raw: string) => void;
type StateListener = (state: ConnectionState, error: string | null) => void;

interface SharedSocketHandle {
  /** Subscribe to every parsed message. Returns an unsubscribe function. */
  onMessage(listener: MessageListener): () => void;
  /** Subscribe to connection-state changes. Fires immediately with the current state. */
  onState(listener: StateListener): () => void;
  /** Send a JSON message. No-op if the socket isn't open yet. */
  send(data: unknown): boolean;
  /** Force a fresh reconnect cycle (drops the current socket if any). */
  reconnect(): void;
  /** Current connection state (synchronous read). */
  state(): ConnectionState;
  /** Last reported error message, if any. */
  error(): string | null;
}

const _messageListeners = new Set<MessageListener>();
const _stateListeners = new Set<StateListener>();

let _socket: WebSocket | null = null;
let _refCount = 0;
let _state: ConnectionState = "closed";
let _lastError: string | null = null;
let _reconnectTimer: number | null = null;
let _reconnectAttempts = 0;
let _intentionalClose = false;

function readAccessToken(): string | undefined {
  try {
    const raw = localStorage.getItem(OIDC_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { access_token?: string };
    return parsed.access_token;
  } catch {
    return undefined;
  }
}

function buildUrl(): string {
  if (!WS_AUTH_PARAM) return WS_URL;
  const token = readAccessToken();
  if (!token) return WS_URL;
  const sep = WS_URL.includes("?") ? "&" : "?";
  return `${WS_URL}${sep}${encodeURIComponent(WS_AUTH_PARAM)}=${encodeURIComponent(token)}`;
}

function setState(next: ConnectionState, error: string | null = null) {
  _state = next;
  if (next === "open") _lastError = null;
  else if (error) _lastError = error;
  for (const l of _stateListeners) {
    try {
      l(_state, _lastError);
    } catch {
      /* listener errors should never break other listeners */
    }
  }
}

function clearReconnectTimer() {
  if (_reconnectTimer != null) {
    window.clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
}

function scheduleReconnect() {
  if (_intentionalClose) return;
  if (_refCount <= 0) return;
  if (_reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) return;
  if (_reconnectTimer != null) return;

  const delay = Math.min(
    RECONNECT_BASE_MS * Math.pow(1.5, _reconnectAttempts),
    RECONNECT_MAX_MS,
  );
  _reconnectAttempts += 1;
  _reconnectTimer = window.setTimeout(() => {
    _reconnectTimer = null;
    openSocket();
  }, delay);
}

function openSocket() {
  if (
    _socket &&
    (_socket.readyState === WebSocket.OPEN ||
      _socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  _intentionalClose = false;
  setState("connecting");

  let socket: WebSocket;
  try {
    socket = new WebSocket(buildUrl());
  } catch (err) {
    setState("error", err instanceof Error ? err.message : String(err));
    scheduleReconnect();
    return;
  }
  _socket = socket;

  socket.onopen = () => {
    _reconnectAttempts = 0;
    setState("open");
  };

  socket.onmessage = (evt: MessageEvent) => {
    const raw = typeof evt.data === "string" ? evt.data : "";
    let parsed: unknown = raw;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Non-JSON frames pass through as the raw string. Consumers can
        // decide how to handle them.
      }
    } else {
      parsed = evt.data;
    }
    for (const l of _messageListeners) {
      try {
        l(parsed, raw);
      } catch {
        /* one bad listener shouldn't sink the others */
      }
    }
  };

  socket.onerror = () => {
    // The error event carries no useful detail in the browser. Wait for
    // onclose to report the actual reason.
    setState("error", _lastError ?? "websocket error");
  };

  socket.onclose = (evt: CloseEvent) => {
    const wasIntentional = _intentionalClose;
    _socket = null;
    const reason = evt.reason || `closed (code ${evt.code})`;
    setState("closed", wasIntentional ? null : reason);
    if (!wasIntentional) scheduleReconnect();
  };
}

function teardown() {
  clearReconnectTimer();
  _reconnectAttempts = 0;
  _intentionalClose = true;
  if (_socket) {
    try {
      _socket.close();
    } catch {
      /* already closed */
    }
    _socket = null;
  }
  setState("closed");
}

const HANDLE: SharedSocketHandle = {
  onMessage(listener) {
    _messageListeners.add(listener);
    return () => {
      _messageListeners.delete(listener);
    };
  },
  onState(listener) {
    _stateListeners.add(listener);
    // Fire immediately so consumers don't have to mirror state in their
    // own useState initial values.
    try {
      listener(_state, _lastError);
    } catch {
      /* ignore */
    }
    return () => {
      _stateListeners.delete(listener);
    };
  },
  send(data) {
    if (!_socket || _socket.readyState !== WebSocket.OPEN) return false;
    const frame = typeof data === "string" ? data : JSON.stringify(data);
    _socket.send(frame);
    return true;
  },
  reconnect() {
    _reconnectAttempts = 0;
    if (_socket) {
      _intentionalClose = true;
      try {
        _socket.close();
      } catch {
        /* ignore */
      }
      _socket = null;
    }
    if (_refCount > 0) openSocket();
  },
  state() {
    return _state;
  },
  error() {
    return _lastError;
  },
};

/**
 * Acquire the shared WebSocket. The first caller opens the connection;
 * later callers piggy-back on the same instance. Pair with
 * `releaseOcrSocket` in cleanup.
 */
export function acquireOcrSocket(): SharedSocketHandle {
  _refCount += 1;
  if (_refCount === 1) openSocket();
  return HANDLE;
}

/**
 * Release a previously acquired socket. Closes the underlying connection
 * when the reference count drops to zero, so we don't leak a live WS
 * during navigation away from OCR-only pages.
 */
export function releaseOcrSocket(): void {
  _refCount = Math.max(0, _refCount - 1);
  if (_refCount === 0) teardown();
}

/**
 * Read-only handle on the shared socket. Useful for one-shot send/inspect
 * calls outside a hook lifecycle. Returns null when nothing is acquired.
 */
export function getOcrSocket(): SharedSocketHandle | null {
  return _refCount > 0 ? HANDLE : null;
}
